import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnly, NotViewOnly } from "@/components/Guards";
import DashboardLayout from "@/components/DashboardLayout";
import { FeatureRoute, PlanRoute } from "@/lib/featureFlags";
import RootRedirect from "@/components/RootRedirect";
import AdminActivityTracker from "@/components/AdminActivityTracker";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import InviteAccept from "@/pages/auth/InviteAccept";
import PendingStatus from "@/pages/auth/PendingStatus";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import NotificationsPage from "@/pages/shared/NotificationsPage";
import ProfilePage from "@/pages/shared/ProfilePage";
import DocumentDepot from "@/pages/shared/DocumentDepot";
import Ledger from "@/pages/shared/Ledger";

import MemberDashboard from "@/pages/member/Dashboard";
import MemberMerits from "@/pages/member/Merits";
import MemberLettering from "@/pages/member/Lettering";
import MemberThespian from "@/pages/member/Thespian";
import ThespianReview from "@/pages/coach/ThespianReview";
import ThespianActivities from "@/pages/coach/ThespianActivities";
import MemberSchedule from "@/pages/member/Schedule";
import MemberChoices from "@/pages/member/Choices";
import MemberAnnouncements from "@/pages/member/Announcements";

import CoachDashboard from "@/pages/coach/Dashboard";
import TeamMembersHub, { MembersTabRedirect } from "@/pages/coach/TeamMembers";
import CoachMerits from "@/pages/coach/Merits";
import CoachMeritOptions from "@/pages/coach/MeritOptions";
import LetteringReview from "@/pages/coach/LetteringReview";
import DocumentReview from "@/pages/coach/DocumentReview";
import DocumentGrid from "@/pages/coach/DocumentGrid";
import CoachSchedule from "@/pages/coach/Schedule";
import CoachChoices from "@/pages/coach/Choices";
import CoachPhonebook from "@/pages/coach/Phonebook";
import CoachAchievements from "@/pages/coach/Achievements";

import AdminDashboard from "@/pages/admin/Dashboard";
import Districts from "@/pages/admin/Districts";
import Organization from "@/pages/admin/Organization";
import Programs from "@/pages/admin/Programs";
import Resources from "@/pages/admin/Resources";
import AdminArchive from "@/pages/admin/Archive";
import SettingsHub from "@/pages/admin/SettingsHub";
import Campuses from "@/pages/admin/Campuses";
import Teams from "@/pages/admin/Teams";
import Achievements from "@/pages/admin/Achievements";
import MeritPresets from "@/pages/admin/MeritPresets";
import Reports from "@/pages/admin/Reports";
import AdminAccounts from "@/pages/admin/Accounts";
import AdminSettings from "@/pages/admin/Settings";
import ShowNotesPage from "@/pages/shared/ShowNotes";
import { StaffDashboard, StaffUsers, StaffPrograms, StaffEmailAudit, StaffDeletionLog, StaffRallyBooks } from "@/pages/staff/StaffPages";

import ParentDashboard from "@/pages/parent/Dashboard";
import ParentStudentLayout from "@/pages/parent/StudentLayout";
import ParentStudentDashboard from "@/pages/parent/StudentDashboard";
import ParentStudentRecord from "@/pages/parent/StudentProfile";
import ParentStudentSchedule from "@/pages/parent/StudentSchedule";
import ParentStudentChoices from "@/pages/parent/StudentChoices";
import ParentStudentInfo from "@/pages/parent/StudentInfo";
import ParentStudentNotifications from "@/pages/parent/StudentNotifications";
import { ParentStudentLedger } from "@/pages/shared/Ledger";
import StudentDocumentDepot from "@/pages/parent/StudentDocumentDepot";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AdminActivityTracker />
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/invite/:token" element={<InviteAccept />} />
            <Route path="/pending" element={<PendingStatus />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Team member */}
            <Route path="/member" element={<ProtectedRoute roles={["team_member"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/member/dashboard" replace />} />
              <Route path="dashboard" element={<MemberDashboard />} />
              <Route path="merits" element={<FeatureRoute flag="merit_tracking" name="Merit Tracking"><MemberMerits /></FeatureRoute>} />
              <Route path="lettering" element={<FeatureRoute flag="lettering" name="Lettering Points"><MemberLettering /></FeatureRoute>} />
              <Route path="thespian" element={<FeatureRoute flag="thespian" name="Thespian Point System"><MemberThespian /></FeatureRoute>} />
              <Route path="show-notes" element={<FeatureRoute flag="show_notes" name="Show Notes"><ShowNotesPage /></FeatureRoute>} />
              <Route path="schedule" element={<FeatureRoute flag="schedule" name="Daily Schedule"><MemberSchedule /></FeatureRoute>} />
              <Route path="choices" element={<MemberChoices />} />
              <Route path="announcements" element={<MemberAnnouncements />} />
              <Route path="document-depot" element={<DocumentDepot />} />
              <Route path="ledger" element={<FeatureRoute flag="rally_books" name="Rally Books"><Ledger /></FeatureRoute>} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Coach */}
            <Route path="/coach" element={<ProtectedRoute roles={["coach"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/coach/dashboard" replace />} />
              <Route path="dashboard" element={<CoachDashboard />} />
              <Route path="members" element={<TeamMembersHub />} />
              <Route path="requests" element={<MembersTabRedirect tab="roster" />} />
              <Route path="parents" element={<MembersTabRedirect tab="parents" />} />
              <Route path="groups" element={<MembersTabRedirect tab="groups" />} />
              <Route path="merits" element={<FeatureRoute flag="merit_tracking" name="Merit Tracking"><CoachMerits /></FeatureRoute>} />
              <Route path="merit-options" element={<PlanRoute hideOn={["base"]} name="Merit Options"><CoachMeritOptions /></PlanRoute>} />
              <Route path="lettering" element={<FeatureRoute flag="lettering" name="Lettering Points"><LetteringReview /></FeatureRoute>} />
              <Route path="thespian" element={<FeatureRoute flag="thespian" name="Thespian Point System"><ThespianReview /></FeatureRoute>} />
              <Route path="thespian/activities" element={<FeatureRoute flag="thespian" name="Thespian Point System"><ThespianActivities /></FeatureRoute>} />
              <Route path="show-notes" element={<FeatureRoute flag="show_notes" name="Show Notes"><ShowNotesPage /></FeatureRoute>} />
              <Route path="bios" element={<MembersTabRedirect tab="bios" />} />
              <Route path="achievements" element={<PlanRoute hideOn={["base"]} name="Achievement Options"><CoachAchievements /></PlanRoute>} />
              <Route path="schedule" element={<FeatureRoute flag="schedule" name="Daily Schedule"><CoachSchedule /></FeatureRoute>} />
              <Route path="choices" element={<CoachChoices />} />
              <Route path="phonebook" element={<FeatureRoute flag="phonebook" name="Phonebook"><CoachPhonebook /></FeatureRoute>} />
              <Route path="document-depot" element={<DocumentDepot />} />
              <Route path="document-review" element={<FeatureRoute flag="document_depot" name="Document Depot"><DocumentReview /></FeatureRoute>} />
              <Route path="document-grid" element={<FeatureRoute flag="document_depot" name="Document Depot"><DocumentGrid /></FeatureRoute>} />
              <Route path="attendance" element={<Navigate to="/coach/schedule?tab=attendance" replace />} />
              <Route path="conflicts" element={<Navigate to="/coach/schedule?tab=conflicts" replace />} />
              <Route path="ledger" element={<FeatureRoute flag="rally_books" name="Rally Books"><Ledger /></FeatureRoute>} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<NotViewOnly><ProfilePage /></NotViewOnly>} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={["administrator"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="organization" element={<Organization />} />
              <Route path="programs" element={<Programs />} />
              <Route path="resources" element={<Resources />} />
              <Route path="districts" element={<Districts />} />
              <Route path="campuses" element={<Campuses />} />
              <Route path="teams" element={<Teams />} />
              <Route path="achievements" element={<Achievements />} />
              <Route path="merit-presets" element={<MeritPresets />} />
              <Route path="reports" element={<Reports />} />
              <Route path="archive" element={<AdminArchive />} />
              <Route path="document-depot" element={<DocumentDepot />} />
              <Route path="ledger" element={<Ledger />} />
              <Route path="accounts" element={<AdminAccounts />} />
              <Route path="settings" element={<SettingsHub />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* District / Campus Administrator */}
            <Route path="/staff" element={<ProtectedRoute roles={["district_administrator", "campus_administrator"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/staff/dashboard" replace />} />
              <Route path="dashboard" element={<StaffDashboard />} />
              <Route path="users" element={<StaffUsers />} />
              <Route path="programs" element={<StaffPrograms />} />
              <Route path="rally-books" element={<StaffRallyBooks />} />
              <Route path="email-audit" element={<StaffEmailAudit />} />
              <Route path="deletion-log" element={<StaffDeletionLog />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            {/* Parent */}
            <Route path="/parent" element={<ProtectedRoute roles={["parent"]}><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/parent/dashboard" replace />} />
              <Route path="dashboard" element={<ParentDashboard />} />
              <Route path="students/:id" element={<ParentStudentLayout />}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<ParentStudentDashboard />} />
                <Route path="record" element={<ParentStudentRecord />} />
                <Route path="schedule" element={<FeatureRoute flag="schedule" name="Daily Schedule"><ParentStudentSchedule /></FeatureRoute>} />
                <Route path="choices" element={<ParentStudentChoices />} />
                <Route path="rally-books" element={<ParentStudentLedger />} />
                <Route path="document-depot" element={<StudentDocumentDepot />} />
                <Route path="profile" element={<ParentStudentInfo />} />
                <Route path="notifications" element={<ParentStudentNotifications />} />
              </Route>
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="profile" element={<ProfilePage />} />
            </Route>

            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
